import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import Pre from "@ui/pre/Pre";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeftRight, CalendarDays, FileJson, Info, RotateCw, ShieldAlert } from "lucide-react";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import Panel from "@ui/panel/Panel";
import { BankAnalysisError, BankAnalysisRequest, loadBankAnalysis, loadEmailInfo } from "./overviewBankAnalysis";
import { parseBankAnalysis } from "./bankDeclineData";
import { BankAnalysisDetails } from "./BankAnalysisDetails";
import { EmailInfoDetails, SectionLabel } from "./EmailInfoDetails";
import { parseEmailInfo } from "./emailInfoData";
import { AnalysisPeriodCaption } from "./AnalysisPeriodCaption";
import { useTranslation } from "react-i18next";
import { getAppLanguage } from "src/i18n";
import { CountryFlag } from "src/view/conponents/icons/CountryFlag";
import { ReserveRefreshButton } from "./styled";
import { EntityBanAction } from "../bans/EntityBanAction";
import { Sessions } from "../forms/Sessions";
import { TransactionPopup } from "../TransactionPreview";
import type { IPaymentSession } from "src/types/types";
import { TransactionNavigationContext } from "../transactionNavigationContext";
import { RuleBlocksTab } from "../pages/administration/RuleBlocksTab";
import styled from "styled-components";

const TabBody = styled.div`
	min-width: 0; padding: 16px 20px 20px;
	@media (max-width: 480px) { padding: 14px 14px 16px; }
`;
/** Cards stand out from a tinted tab, as in the rule details popup. */
const InfoBody = styled(TabBody)`flex: 1 0 auto; padding-top: 20px; background: #f5f7fa;`;

export const BankAnalysisPopup = ({ request, onClose, onEntity }: { request: BankAnalysisRequest; onClose: () => void; onEntity?: (entity: BankAnalysisRequest["bank"]) => void }) => {
	 useLocale();
	const { t } = useTranslation();
	const exitTransaction = useContext(TransactionNavigationContext);
	const popup = useRef<HTMLDivElement>(null);
	const transactionPopup = useRef<HTMLDivElement>(null);
 const requestKey = JSON.stringify([request.bank.type, request.bank.value, request.from, request.to, request.currency, request.userId, request.projectId]);
	const [loaded, setState] = useState<{ key: string; busy: boolean; raw?: string; error?: string }>({ key: requestKey, busy: true });
 const state: typeof loaded = loaded.key === requestKey ? loaded : { key: requestKey, busy: true };
	const [emailInfoLoaded, setEmailInfo] = useState<typeof loaded>({ key: requestKey, busy: request.bank.type === "email" });
	const emailInfo = emailInfoLoaded.key === requestKey ? emailInfoLoaded : { key: requestKey, busy: request.bank.type === "email" };
	const busy = state.busy || (request.bank.type === "email" && emailInfo.busy);
	const [attempt, setAttempt] = useState(0);
	const [transaction, setTransaction] = useState<{ key: string; session: IPaymentSession } | null>(null);
	const selectedTransaction = transaction?.key === requestKey ? transaction.session : null;
	const { bank, from, to, currency, userId, projectId } = request;
	const title = t(`bankAnalysis.titles.${bank.type}`, { name: bank.label });
	const panelTitle = bank.type === "country" ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{t("bankAnalysis.titles.country", { name: "" }).trim()} <CountryFlag code={bank.value} variant="rectangle" /> {bank.label}</div> : title;
	const analysis = useMemo(() => state.raw ? parseBankAnalysis(state.raw, request) : null, [state.raw, bank.type, bank.value, from, to, currency]);
	const emailDetails = useMemo(() => bank.type === "email" && emailInfo.raw ? parseEmailInfo(emailInfo.raw, request) : null, [emailInfo.raw, bank.type, bank.value, from, to, currency]);
	const transactionFilters = useMemo(() => ({ search: bank.value, from, to, ...(currency ? { currency } : {}) }), [bank.value, from, to, currency]);
	const transactionRequestOptions = useMemo(() => ({ expectedProjectId: projectId }), [projectId]);

	useEffect(() => {
		if (!selectedTransaction) return;
		const previousFocus = document.activeElement;
		transactionPopup.current?.focus({ preventScroll: true });
		return () => {
			if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
		};
	}, [selectedTransaction]);
	useEffect(() => {
		const previousFocus = document.activeElement;
		popup.current?.focus({ preventScroll: true });
		return () => {
			if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
		};
	}, []);
	useEffect(() => {
		let cancelled = false;
		setState(previous => previous.key === requestKey ? { ...previous, busy: true, error: undefined } : { key: requestKey, busy: true });
		void loadBankAnalysis({ bank, from, to, currency, userId, projectId }, attempt > 0).then(
			raw => { if (!cancelled) setState({ key: requestKey, busy: false, raw }); },
			error => { if (!cancelled) setState(previous => ({ key: requestKey, busy: false, error: error instanceof BankAnalysisError && getAppLanguage() === "ru" ? error.message : t("bankAnalysis.loadError"), raw: previous.key === requestKey && previous.raw !== undefined ? previous.raw : error instanceof BankAnalysisError ? error.raw : undefined })); },
		);
		return () => { cancelled = true; };
	}, [bank.type, bank.value, from, to, currency, userId, projectId, attempt, t]);
	useEffect(() => {
		if (bank.type !== "email") return;
		let cancelled = false;
		setEmailInfo(previous => previous.key === requestKey ? { ...previous, busy: true, error: undefined } : { key: requestKey, busy: true });
		void loadEmailInfo({ bank, from, to, currency, userId, projectId }, attempt > 0).then(
			raw => { if (!cancelled) setEmailInfo({ key: requestKey, busy: false, raw }); },
			error => { if (!cancelled) setEmailInfo(previous => ({ key: requestKey, busy: false, error: t("bankAnalysis.loadError"), raw: error instanceof BankAnalysisError && error.raw !== undefined ? error.raw : previous.key === requestKey ? previous.raw : undefined })); },
		);
		return () => { cancelled = true; };
	}, [bank.type, bank.value, from, to, currency, userId, projectId, attempt, t]);

	if (typeof document === "undefined") return null;
	return createPortal(
		<div ref={popup} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} onKeyDown={event => {
			if (event.key === "Escape") { event.stopPropagation(); onClose(); }
		}}>
		<Panel decorator="absolute" label={panelTitle} onClose={onClose}
			sx={{ width: "980px", maxWidth: "94vw", height: "min(860px, 90dvh)", maxHeight: "90dvh", overflow: "hidden" }} growContent disableScroll contentPadding="0">
			<div style={{ minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
				<div role="status" aria-live="polite" aria-busy={state.busy} style={{ padding: "0 20px" }}>
					{state.busy && state.raw === undefined && <p>{t("bankAnalysis.loadingDetails")}</p>}
					{state.error && <p style={{ color: "#b42332" }}>{state.error}</p>}
				</div>
				<Tabs key={requestKey} variant="modern" scrollable
					sx={{ flexShrink: 0, flexWrap: "wrap", alignItems: "stretch", overflow: "visible", rowGap: 12, padding: "20px 20px 0" }}
					decoration={<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", gap: 16 }}>
						<EntityBanAction type={bank.type} value={bank.value} projectId={projectId} userId={userId} />
						<div style={{ color: "#667085", display: "flex", alignItems: "center", gap: 8 }}><CalendarDays size={15} style={{ flexShrink: 0 }} aria-hidden="true" /><AnalysisPeriodCaption from={from} to={to} currency={bank.type === "email" ? currency : analysis?.currency ?? currency} /></div>
						<ReserveRefreshButton $busy={busy} disabled={busy} title={state.error || emailInfo.error ? t("common.retry") : t("common.refresh")} aria-label={state.error || emailInfo.error ? t("common.retry") : t("common.refresh")} onClick={() => setAttempt(value => value + 1)}><RotateCw size={16} aria-hidden="true" /></ReserveRefreshButton>
					</div>}>
					<TabItem title={msg("info_170322a3")} icon={<Info size={16} aria-hidden="true" />}>
						<InfoBody>
							{bank.type === "email" && <div aria-busy={emailInfo.busy}>
								<div role="status" aria-live="polite">
									{emailInfo.busy && !emailDetails && <p>{t("emailInfo.loading")}</p>}
									{emailInfo.error && <p style={{ color: "#b42332" }}>{t("emailInfo.loadError")}</p>}
									{emailInfo.raw !== undefined && !emailDetails && !emailInfo.error && <p>{t("bankAnalysis.responseMismatch")}</p>}
								</div>
								{emailDetails && <EmailInfoDetails key={requestKey} data={emailDetails} />}
							</div>}
							{bank.type === "email" && analysis && <SectionLabel>{t("bankAnalysis.declines")}</SectionLabel>}
							{analysis && <BankAnalysisDetails key={requestKey} data={analysis} onEntity={onEntity} />}
							{state.raw !== undefined && !analysis && !state.error && <p style={{ color: "#667085", fontSize: 13, lineHeight: 1.6 }}>{t("bankAnalysis.responseMismatch")}</p>}
						</InfoBody>
					</TabItem>
					{bank.type === "email" && <TabItem title={t("bankAnalysis.transactions")} icon={<ArrowLeftRight size={16} aria-hidden="true" />}>
						<TabBody style={{ minHeight: 0, display: "flex", flexDirection: "column", flex: "1 1 0" }}>
							<Sessions key={`${requestKey}:${attempt}`} target_id={projectId} target_type="project"
								onOpenSession={session => setTransaction({ key: requestKey, session })}
								filterable={false} tableScrollable showSummary={false} fixedFilters={transactionFilters} requestOptions={transactionRequestOptions} />
						</TabBody>
					</TabItem>}
					{bank.type === "email" && <TabItem title={t("bankAnalysis.rules")} icon={<ShieldAlert size={16} aria-hidden="true" />}>
						<TabBody>
							<RuleBlocksTab key={`${requestKey}:${attempt}`} projectId={projectId} userId={userId} scope={{ entity_type: "email", entity_value: bank.value, from, to }} />
						</TabBody>
					</TabItem>}
					<TabItem title={msg("raw")} icon={<FileJson size={16} aria-hidden="true" />}>
						<TabBody>
							{state.raw !== undefined ? <Pre data={state.raw} wrap label={t("bankAnalysis.crmResponse")} style={{ marginTop: 12 }} /> : <p style={{ color: "#667085", fontSize: 13 }}>{state.busy ? t("bankAnalysis.waitingResponse") : t("bankAnalysis.rawUnavailable")}</p>}
							{bank.type === "email" && <div aria-busy={emailInfo.busy}>
								<p style={{ fontSize: 13 }}>overview/email/info</p>
								{emailInfo.error && <p role="status" style={{ color: "#b42332" }}>{emailInfo.error}</p>}
								{emailInfo.raw !== undefined ? <Pre data={emailInfo.raw} wrap label="overview/email/info" style={{ marginTop: 12 }} /> : <p style={{ color: "#667085", fontSize: 13 }}>{emailInfo.busy ? t("bankAnalysis.waitingResponse") : t("bankAnalysis.rawUnavailable")}</p>}
							</div>}
						</TabBody>
					</TabItem>
				</Tabs>
			</div>
		</Panel>
		{selectedTransaction && <div ref={transactionPopup} role="dialog" aria-modal="true" aria-label={msg("templatePayment_c88c6d69", { v0: selectedTransaction.id })} tabIndex={-1} onKeyDown={event => {
			if (event.key === "Escape") { event.stopPropagation(); setTransaction(null); }
		}}>
			<TransactionPopup key={selectedTransaction.id} session={selectedTransaction} onClose={() => setTransaction(null)} onOpenSession={session => setTransaction({ key: requestKey, session })} onExit={() => { onClose(); exitTransaction?.(); }} />
		</div>}
		</div>, document.body,
	);
};
